/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {RefObject, useEffect, useState} from 'react';

// how far the covering element's top right corner is rounded - the root
// runs that far under it, so the corner shows the root rather than a gap ...
const getCornerRadius = (cover: HTMLElement): number => (
  parseFloat(getComputedStyle(cover).borderTopRightRadius) || 0
);

// the inset that puts the root's left edge under the covering element's
// rounded right edge - none once the element no longer reaches past the
// root's own left. The root's current inset is taken back out first, so it
// is measured from where it would sit without one ...
const measureInset = (root: HTMLElement | null, cover: HTMLElement | null, current: number): number => {
  let inset = 0;
  if (root && cover) {
    const baseLeft = root.getBoundingClientRect().left - current;
    const right    = cover.getBoundingClientRect().right - getCornerRadius(cover);
    inset          = Math.max(Math.round(right - baseLeft), 0);
  }
  return inset;
};

/**
 * Measures how far an element (`elementId`) covers a root's left side, so the
 * root can start where that element ends - the applied filter bar beside the
 * open filter drawer, say - running under its rounded top right corner, so
 * the corner shows the root rather than a gap. The element must sit above
 * the root. The element is followed every frame while an id is
 * given, as a drawer slides in; with none, the inset is `0`.
 *
 * ```typescript
 *
 *   import useAvoidElementInset from '@ion/dls/src/filters/useAvoidElementInset';
 *   ...
 *
 *   const leftInset = useAvoidElementInset(rootRef, drawerOpen ? 'filter-drawer-paper' : undefined);
 *
 * ```
 *
 * @param rootRef   The element that steps aside.
 * @param elementId The covering element's id, or none while nothing covers it.
 * @returns         The left inset, in pixels.
 */
export const useAvoidElementInset = (rootRef: RefObject<HTMLElement>, elementId?: string): number => {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    let frame = 0;
    const track = function() {
      setInset(function(current) {
        return measureInset(rootRef.current, document.getElementById(elementId), current);
      });
      frame = window.requestAnimationFrame(track);
    };
    if (elementId) {
      frame = window.requestAnimationFrame(track);
    } else {
      setInset(0);
    }
    return function() {
      window.cancelAnimationFrame(frame);
    };
  }, [elementId, rootRef]);

  return inset;
};

export default useAvoidElementInset;
